import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { COUNTRIES, filterCountries } from "../lib/countries";

const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";

// Shared searchable country combobox (complete ISO 3166-1 alpha-2 list from
// lib/countries — the single authoritative source). Keyboard accessible:
// arrows move, Enter selects, Escape closes. Used by both the Individual Host
// and Commercial Host address workflows.
export default function CountryPicker({ value, onChange, testId = "country-picker", disabled = false, autoOpen = false }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(autoOpen);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const selected = COUNTRIES.find(([c]) => c === value);
  const list = filterCountries(q).slice(0, 60);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && list[active]) { e.preventDefault(); onChange(list[active][0]); setOpen(false); setQ(""); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} onKeyDown={onKey}>
      <button type="button" disabled={disabled} onClick={() => { setOpen((o) => !o); setActive(0); }}
              data-testid={testId} aria-haspopup="listbox" aria-expanded={open}
              className={`${inputCls} text-left flex items-center justify-between gap-2 disabled:opacity-50`}>
        <span className="truncate">
          {selected
            ? <>{selected[1]} <span className="text-zinc-500 text-xs">({value})</span></>
            : <span className="text-zinc-500">Select country</span>}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-40 mt-2 w-full card-dark p-2 shadow-2xl animate-[fadeIn_.15s_ease-out]" role="listbox"
             aria-label="Countries" data-testid={`${testId}-list`}>
          <div className="relative mb-1.5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }} placeholder="Search countries…"
                   className={`${inputCls} !py-2 !pl-9`} data-testid={`${testId}-search`} aria-label="Search countries" />
          </div>
          <div className="max-h-56 overflow-auto">
            {list.map(([code, name], i) => (
              <button key={code} type="button" role="option" aria-selected={code === value}
                      data-testid={`${testId}-${code}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => { onChange(code); setOpen(false); setQ(""); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        code === value ? "bg-[#E50914]/15 text-white" : i === active ? "bg-[#27272A] text-white" : "text-zinc-300 hover:bg-[#27272A]"}`}>
                {name} <span className="text-zinc-500 text-xs">({code})</span>
              </button>
            ))}
            {list.length === 0 && <p className="px-3 py-2 text-xs text-zinc-500" data-testid={`${testId}-empty`}>No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}
